import { apiUrl } from "../lib/api.js";
import { SUBMISSION_LIMITS } from "@rp/shared";

/**
 * The page the person who paid actually lands on.
 *
 * They have no account and never will: they paid on a streamer's tip page and
 * now need to upload one file, once. So this is the only screen in the
 * dashboard that runs without a session — the token in the link is the whole
 * credential, and it grants exactly one thing.
 *
 * It exists because the flow did not work without it. The email sends them to
 * `#/submit?t=…`, the router had no such route, and they landed on a login
 * screen for an account they do not have. Everything either side of this page
 * was built and tested; the step where the person who had already paid does
 * their part was a link to nowhere.
 *
 * Written for them rather than for the streamer. They are not a customer of
 * ours, they have no idea what Stream-Cut is, and they have already handed over
 * money — so every message says what is happening, whose turn it is, and what
 * to do if it goes wrong.
 */

const esc = (v: string) =>
  v.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!,
  );

/** 16 MiB, matching the recording uploader: small enough to retry cheaply. */
const PART_SIZE = 16 * 1024 * 1024;

interface Brief {
  state: string;
  /** What the payment bought, decided by the tier its amount covered. */
  kind: "reaction" | "promotion" | "both";
  kind_label: string;
  max_seconds: number;
  max_bytes: number;
  min_resolution: string;
  format: string;
  /** False only for a promotion, which can be a link and nothing else. */
  video_required: boolean;
  needs_link: boolean;
  promo_label: string;
  promo_link: string;
}

/**
 * What each state means to the person who paid.
 *
 * The enum is written from the system's side. Somebody who has paid and opened
 * a link needs to know whether it is their turn, and these say so.
 */
const WHERE_WE_ARE: Record<string, string> = {
  paid: "",
  awaiting_upload: "",
  validating: "Your video is being checked. This takes a minute or two.",
  pending_review:
    "Your video is with the streamer. They decide what goes on their stream, " +
    "and they will see it before their next broadcast.",
  approved: "The streamer has accepted it. It plays on their next stream.",
  played: "This one has already been on stream. Thanks for sending it.",
  rejected:
    "The streamer passed on this one. Ask them for a refund through the same " +
    "page you paid on — the money went to them, not to Stream-Cut.",
  expired:
    "This link has expired. Ask the streamer for a refund through the page you " +
    "paid on.",
  withdrawn: "This submission was withdrawn.",
};

export async function submitView(root: HTMLElement, token: string): Promise<void> {
  // The id is the part before the signature. Taken from the token rather than
  // the URL so there is only one thing to get wrong, and the server checks the
  // two against each other anyway.
  const cut = token.lastIndexOf(".");
  const id = cut > 0 ? token.slice(0, cut) : "";

  root.innerHTML = `<div class="submit-shell">
      <h1>Upload your video</h1>
      <p class="meta" id="sState">Checking your link…</p>
      <div id="sBody"></div>
    </div>`;

  const state = root.querySelector<HTMLElement>("#sState")!;
  const body = root.querySelector<HTMLElement>("#sBody")!;

  if (!id) {
    state.textContent =
      "That link is not complete. Use the whole link from the email, including " +
      "everything after the question mark.";
    return;
  }

  /** Every call carries the token and nothing else. */
  const call = async <T>(method: string, path: string, payload?: unknown): Promise<T> => {
    const res = await fetch(apiUrl(path), {
      method,
      headers: {
        authorization: `Bearer ${token}`,
        ...(payload !== undefined ? { "content-type": "application/json" } : {}),
      },
      body: payload !== undefined ? JSON.stringify(payload) : undefined,
    });
    const json = await res.json().catch(() => null);
    if (!res.ok) {
      throw new Error(
        (json as { message?: string } | null)?.message ??
          `That did not work (${res.status}).`,
      );
    }
    return json as T;
  };

  let brief: Brief;
  try {
    brief = await call<Brief>("GET", `/v1/submissions/${id}/upload`);
  } catch {
    // A bad signature and an unknown id look the same on purpose, so the
    // message cannot say which.
    state.textContent =
      "That link is not valid any more. If you paid and cannot upload, ask the " +
      "streamer for a refund through the page you paid on.";
    return;
  }

  const where = WHERE_WE_ARE[brief.state] ?? "";
  if (where) {
    // Past the upload step. Nothing to do here but say where it got to.
    state.textContent = where;
    return;
  }

  const mb = Math.round(brief.max_bytes / 1024 / 1024);
  state.textContent =
    `You paid for: ${brief.kind_label.toLowerCase()}. The streamer decides what ` +
    `goes on their stream, so they will see it before anything is broadcast.`;

  body.innerHTML = `
    ${
      brief.needs_link
        ? `<div class="submit-promo">
             <label>What should the button say?
               <input id="sLabel" maxlength="80" value="${esc(brief.promo_label)}"
                      placeholder="Your product, or your name">
             </label>
             <label>Where should it go?
               <input id="sLink" spellcheck="false" value="${esc(brief.promo_link)}"
                      placeholder="yourshop.com/thing">
             </label>
             <p class="meta">This becomes a button on the phone of everybody watching
                — they opened the stream's room code to tap reactions, so it is the one
                place a link on a live stream is actually tappable. It will be labelled
                as a paid promotion, which the streamer cannot remove and neither can
                we.</p>
           </div>`
        : ""
    }

    <ul class="submit-rules">
      ${
        brief.video_required
          ? `<li>Up to <strong>${brief.max_seconds} seconds</strong>, and at least
               ${Math.round(SUBMISSION_LIMITS.minMs / 1000)}</li>`
          : `<li>A video is <strong>optional</strong> — with one it plays in a corner of
               the stream, without one your button still goes out. Up to
               ${brief.max_seconds} seconds if you send one</li>`
      }
      <li><strong>${esc(brief.format)}</strong> — what a phone or any editor exports by default</li>
      <li>At least ${esc(brief.min_resolution)}, up to ${mb} MB</li>
      <li>It must be yours to send. Anything you do not have the right to use
          will be turned down, and the streamer is the one who takes the risk</li>
    </ul>

    <input type="file" id="sFile" accept="video/mp4,video/*" hidden>
    <div class="actions">
      <button class="btn" id="sPick">${
        brief.video_required ? "Choose your video" : "Add a video (optional)"
      }</button>
      <button class="btn" id="sSend"${brief.video_required ? " hidden" : ""}>Send it</button>
    </div>
    <div class="bar" id="sBar" hidden><i></i></div>
    <p class="meta" id="sNote"></p>`;

  const file = body.querySelector<HTMLInputElement>("#sFile")!;
  const pick = body.querySelector<HTMLButtonElement>("#sPick")!;
  const send = body.querySelector<HTMLButtonElement>("#sSend")!;
  const bar = body.querySelector<HTMLElement>("#sBar")!;
  const fill = bar.querySelector<HTMLElement>("i")!;
  const note = body.querySelector<HTMLElement>("#sNote")!;

  pick.addEventListener("click", () => file.click());

  file.addEventListener("change", () => {
    const chosen = file.files?.[0];
    if (!chosen) return;
    // Checked here as well as server-side, because the server cannot refuse it
    // until the first request and the honest moment to say "too big" is before
    // anything has been uploaded.
    if (chosen.size > brief.max_bytes) {
      note.textContent =
        `That file is ${Math.round(chosen.size / 1024 / 1024)} MB and the limit ` +
        `is ${mb} MB. A shorter clip, or exported smaller, will go through.`;
      send.hidden = true;
      return;
    }
    pick.textContent = chosen.name;
    note.textContent = `${Math.round(chosen.size / 1024 / 1024)} MB, ready to send.`;
    send.hidden = false;
  });

  send.addEventListener("click", async () => {
    const chosen = file.files?.[0];
    // A promotion can go without one. A reaction cannot, and the button stays
    // hidden until a file is chosen, so this is the promotion case.
    if (!chosen && brief.video_required) return;

    pick.disabled = true;
    send.disabled = true;
    note.textContent = "Starting…";

    try {
      // The link first, and before any upload: it is the part that can be
      // refused for a reason they can fix, and finding that out after a
      // fifteen-minute upload would be the wrong order.
      if (brief.needs_link) {
        const label = body.querySelector<HTMLInputElement>("#sLabel")!.value;
        const link = body.querySelector<HTMLInputElement>("#sLink")!.value;
        await call("PUT", `/v1/submissions/${id}/promo`, { label, link });
      }

      if (!chosen) {
        await call("POST", `/v1/submissions/${id}/submit`);
        body.innerHTML = `<p class="meta"><strong>Sent.</strong> Your button is with
          the streamer. They decide what goes on their stream, and if they pass on
          it, ask them for a refund through the page you paid on — the money went to
          them, not to Stream-Cut.</p>`;
        return;
      }

      bar.hidden = false;
      const parts = Math.max(1, Math.ceil(chosen.size / PART_SIZE));
      const started = await call<{
        upload_id: string;
        parts: { number: number; url: string }[];
      }>(
        "POST",
        `/v1/submissions/${id}/upload`,
        { parts, size_bytes: chosen.size },
      );

      const etags: { number: number; etag: string }[] = [];
      for (let i = 0; i < started.parts.length; i++) {
        const part = started.parts[i]!;
        // Sliced by the part's own number rather than the loop index. They match
        // today -- the server numbers from one -- and relying on that would make
        // a resumed or partial batch upload the wrong bytes under the right
        // name, which is the kind of corruption nothing would report.
        const from = (part.number - 1) * PART_SIZE;
        const slice = chosen.slice(from, Math.min(from + PART_SIZE, chosen.size));
        const put = await fetch(part.url, { method: "PUT", body: slice });
        if (!put.ok) throw new Error(`Part ${part.number} did not upload. Try again.`);
        const etag = put.headers.get("etag");
        if (!etag) {
          // Without the ETag the upload cannot be completed, and that is a
          // server configuration problem rather than anything they did.
          throw new Error(
            "The upload was accepted but could not be confirmed. Tell the streamer.",
          );
        }
        etags.push({ number: part.number, etag });
        const pct = Math.round(((i + 1) / started.parts.length) * 100);
        fill.style.width = `${pct}%`;
        note.textContent = `Uploading… ${pct}%`;
      }

      note.textContent = "Finishing…";
      await call("POST", `/v1/submissions/${id}/complete`, {
        upload_id: started.upload_id,
        parts: etags,
      });

      fill.style.width = "100%";
      body.innerHTML = `<p class="meta"><strong>Sent.</strong> Your video is being
        checked, and then it goes to the streamer to watch. They decide what plays.
        If they pass on it, ask them for a refund through the page you paid on —
        the money went to them, not to Stream-Cut.</p>`;
    } catch (err) {
      pick.disabled = false;
      send.disabled = false;
      note.textContent = err instanceof Error ? err.message : String(err);
    }
  });
}
